import { useCallback, useEffect, useRef, useState } from "react";
import { exportToCanvas, restoreElements } from "@excalidraw/excalidraw";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert, errorMessage } from "../components/ErrorAlert";
import { Link } from "../components/Link";
import { Modal } from "../components/Modal";
import { classPath } from "../router";

import type { ApiErrorCode, RevisionPreview } from "../api/client";
import type {
  CanvasState,
  Revision,
  SerializedExcalidrawElement,
} from "@canvaschool/contracts";
import type { ExcalidrawElement } from "@excalidraw/excalidraw/element/types";

const SOURCE_LABELS: Record<Revision["source"], string> = {
  AUTO_INTERVAL: "자동 저장",
  AUTO_IDLE: "모두 나간 뒤 저장",
  PRE_RESTORE: "복원 직전",
  RESTORE_RESULT: "복원 결과",
  MANUAL: "선생님이 찍은 기준점",
};

const CONFLICT_MESSAGE =
  "그사이 캔버스가 바뀌었습니다. 최신 상태를 불러왔으니 다시 시도해 주세요.";

const ScenePreview = ({
  scene,
}: {
  scene: readonly SerializedExcalidrawElement[];
}) => {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    void exportToCanvas({
      elements: restoreElements(scene as unknown as ExcalidrawElement[], null),
      appState: { exportBackground: true, viewBackgroundColor: "#ffffff" },
      files: null,
      maxWidthOrHeight: 480,
    }).then((canvas) => {
      if (!cancelled) {
        setUrl(canvas.toDataURL());
      }
    });
    return () => {
      cancelled = true;
    };
  }, [scene]);

  if (url === null) {
    return <p aria-busy="true">미리 보기를 그리는 중…</p>;
  }
  return <img src={url} alt="이 시점의 캔버스" />;
};

export const RevisionPage = ({
  classId,
  canvasId,
}: {
  classId: string;
  canvasId: string;
}) => {
  const auth = useAuth();
  const { api } = auth;
  const [revisions, setRevisions] = useState<Revision[] | null>(null);
  const [canvasState, setCanvasState] = useState<CanvasState | null>(null);
  const [preview, setPreview] = useState<RevisionPreview | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<Revision | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<ApiErrorCode | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [page, state] = await Promise.all([
        api.listRevisions(classId, canvasId),
        api.getCanvasState(classId, canvasId),
      ]);
      setRevisions(page.items);
      setCanvasState(state);
    } catch (failure) {
      setLoadError(auth.handleError(failure));
    }
    // `auth` changes identity on every session update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, classId, canvasId]);

  useEffect(() => {
    void load();
  }, [load]);

  const restoring = useRef(false);
  const restore = async (revision: Revision) => {
    if (restoring.current || !canvasState) {
      return;
    }
    restoring.current = true;
    setError(null);
    setNotice(null);
    try {
      await api.restoreRevision(classId, canvasId, revision.id, {
        expectedEpoch: canvasState.epoch,
        expectedVersion: canvasState.version,
      });
      setRestoreTarget(null);
      void load();
      setNotice("이 시점으로 복원했습니다.");
    } catch (failure) {
      const code = auth.handleError(failure);
      setRestoreTarget(null);
      void load();
      setError(code === "CONFLICT" ? CONFLICT_MESSAGE : errorMessage(code));
    } finally {
      restoring.current = false;
    }
  };

  if (loadError) {
    return (
      <>
        <ErrorAlert code={loadError} />
        <button
          type="button"
          onClick={() => {
            setLoadError(null);
            void load();
          }}
        >
          다시 시도
        </button>
      </>
    );
  }

  return (
    <div className="canvaschool-page">
      <h1>캔버스 이력</h1>
      <p>
        <Link to={classPath(classId)}>학급으로 돌아가기</Link>
      </p>
      {notice && (
        <p className="canvaschool-success" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="canvaschool-error" role="alert">
          {error}
        </p>
      )}
      <div className="canvaschool-revision-layout">
        {revisions === null ? (
          <p aria-busy="true">불러오는 중…</p>
        ) : revisions.length === 0 ? (
          <p>아직 저장된 이력이 없습니다.</p>
        ) : (
          <ol className="canvaschool-list">
            {revisions.map((revision) => (
              <li key={revision.id} className="canvaschool-revision-item">
                <span>{SOURCE_LABELS[revision.source]}</span>
                <span className="canvaschool-meta">
                  {new Date(revision.createdAt).toLocaleString("ko-KR")}
                </span>
                <span className="canvaschool-meta">
                  버전 {revision.version}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setPreview(
                        await api.previewRevision(
                          classId,
                          canvasId,
                          revision.id,
                        ),
                      );
                    } catch (failure) {
                      setError(errorMessage(auth.handleError(failure)));
                    }
                  }}
                >
                  미리 보기
                </button>
                {canvasState?.mode !== "READ_ONLY" && (
                  <button
                    type="button"
                    className="canvaschool-btn-primary"
                    onClick={() => setRestoreTarget(revision)}
                  >
                    이 시점으로 복원
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
        {preview && (
          <section
            className="canvaschool-revision-preview"
            aria-labelledby="canvaschool-preview-heading"
          >
            <h2 id="canvaschool-preview-heading">미리 보기</h2>
            <ScenePreview scene={preview.scene} />
            <p>
              {SOURCE_LABELS[preview.revision.source]} · 버전{" "}
              {preview.revision.version}
            </p>
            <p>
              요소{" "}
              {preview.scene.filter((element) => !element.isDeleted).length}개
            </p>
            <p>이미지 {preview.assetIds.length}개</p>
          </section>
        )}
      </div>
      {restoreTarget && (
        <Modal
          title="이력 복원"
          primaryLabel="복원"
          onCancel={() => setRestoreTarget(null)}
          onPrimary={() => void restore(restoreTarget)}
        >
          <div className="canvaschool-modal-info-box">
            <p>이 이력으로 복원할까요?</p>
            <p className="canvaschool-hint">
              접속 중인 학생의 캔버스에도 즉시 복원 내용이 동기화됩니다.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};
